import React, { useCallback, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DateTime } from 'luxon';
import {
  MRT_ColumnDef,
  MRT_Table,
  useMaterialReactTable,
} from 'material-react-table';
import { formatDateTime } from '../../helpers';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMaterialReactTableLocalization } from '../../i18n';
import { Button } from '../design-system';

type AccessToken = {
  id: string;
  name: string;
  lastUsedAt: string;
  createdAt: string;
  expiresAt: string;
  token: string;
};

type CopyButtonProps = {
  value: string;
};

interface RemoveMutationOptions {
  variables: { id: string };
}

interface AccessTokenGridProps {
  data: AccessToken[];
  onRemove: (options: RemoveMutationOptions) => Promise<unknown>;
}

const copyButtonStyles = {
  p: 0,
  width: 22,
  height: 22,
  '&:hover': {
    backgroundColor: 'action.hover',
  },
};
const copyIconStyles = { fontSize: 14 };
const primaryRowStyles = {
  alignItems: 'center',
  display: 'flex',
  minHeight: 24,
};
const actionRowStyles = {
  ...primaryRowStyles,
  justifyContent: 'flex-end',
};

type CopyFeedbackProps = {
  open: boolean;
};

const HIDDEN_TOKEN_CHARACTERS = '••••••••';

export const CopyFeedback = ({ open }: CopyFeedbackProps) => {
  return (
    <Fade in={open} timeout={150}>
      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        sx={{
          color: 'success.main',
        }}
      >
        <CheckIcon sx={{ fontSize: 16 }} />
        <Typography variant="caption" sx={{ fontSize: 12, fontWeight: 500 }}>
          <FormattedMessage
            id="account.apiKeys.copied"
            defaultMessage="Copied to clipboard"
          />
        </Typography>
      </Box>
    </Fade>
  );
};

export const CopyToClipboardButton = ({ value }: CopyButtonProps) => {
  const intl = useIntl();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      console.error('Copy failed', e);
    }
  };

  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <IconButton
        aria-label={intl.formatMessage({
          id: 'account.apiKeys.copy',
          defaultMessage: 'Copy API key',
        })}
        onClick={handleCopy}
        disableRipple
        disableFocusRipple
        size="small"
        sx={copyButtonStyles}
      >
        <ContentCopyIcon sx={copyIconStyles} />
      </IconButton>
      <CopyFeedback open={copied} />
    </Stack>
  );
};

interface RemoveAccessTokenButtonProps {
  id: string;
  onRemove: AccessTokenGridProps['onRemove'];
}

const RemoveAccessTokenButton = React.memo(
  ({ id, onRemove }: RemoveAccessTokenButtonProps) => {
    const handleRemove = useCallback(async () => {
      await onRemove({ variables: { id } });
    }, [id, onRemove]);

    return (
      <Button
        density="compact"
        startIcon={<DeleteOutlineIcon />}
        tone="danger"
        variant="outlined"
        onClick={handleRemove}
      >
        <FormattedMessage id="general.remove" defaultMessage="Remove" />
      </Button>
    );
  }
);

RemoveAccessTokenButton.displayName = 'RemoveAccessTokenButton';

export const AccessTokenGrid = ({ data, onRemove }: AccessTokenGridProps) => {
  const intl = useIntl();
  const localization = useMaterialReactTableLocalization();
  const columns = useMemo<MRT_ColumnDef<AccessToken>[]>(
    () => [
      {
        accessorKey: 'name',
        header: intl.formatMessage({
          id: 'account.apiKeys.token',
          defaultMessage: 'Token',
        }),
        Cell: ({ cell, row }) => {
          const record = row.original;
          return (
            <Stack direction="column" gap={1}>
              <Box sx={primaryRowStyles}>
                {cell.getValue<string>()} -{' '}
                {record.expiresAt
                  ? formatDateTime(record.expiresAt)
                  : intl.formatMessage({
                      id: 'account.apiKeys.neverExpires',
                      defaultMessage: 'Never expires',
                    })}
              </Box>
              <Stack direction="row" fontSize="0.7rem" gap={1}>
                <FormattedMessage
                  id="account.apiKeys.key"
                  defaultMessage="Key"
                />
                : <b>{record.token ?? HIDDEN_TOKEN_CHARACTERS}</b>{' '}
                <CopyToClipboardButton value={row.original.token || 'flop'} />
              </Stack>
            </Stack>
          );
        },
      },
      {
        Cell: ({ cell }) => (
          <Box sx={primaryRowStyles}>
            {cell.getValue<Date | null>()
              ? DateTime.fromJSDate(cell.getValue<Date>()).toRelative({
                  locale: intl.locale,
                })
              : '-'}
          </Box>
        ),
        accessorFn: (row) => row.lastUsedAt && new Date(row.lastUsedAt),
        header: intl.formatMessage({
          id: 'account.apiKeys.lastUsed',
          defaultMessage: 'Last used',
        }),
        size: 120,
      },
      {
        id: 'actions',
        header: '',
        Cell: ({ row }) => (
          <Box sx={actionRowStyles}>
            <RemoveAccessTokenButton
              id={row.original.id}
              onRemove={onRemove}
            />
          </Box>
        ),
        enableSorting: false,
        muiTableBodyCellProps: { align: 'right' },
        muiTableHeadCellProps: { align: 'right' },
        size: 120,
      },
    ],
    [intl, onRemove]
  );

  const table = useMaterialReactTable({
    columns,
    data,
    localization,
    enableColumnActions: false,
    enableTableHead: true,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: false,
    muiTableHeadCellProps: {
      sx: { fontSize: '0.8rem' },
    },
    muiTableBodyRowProps: { hover: false },
    muiTableHeadRowProps: {
      sx: {
        boxShadow: 'none',
      },
    },
    muiTableProps: {
      sx: {
        border: 'none',
        caption: {
          captionSide: 'top',
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        fontSize: '0.8rem',
        border: 'none',
        verticalAlign: 'top',
      },
    },
  });

  return <MRT_Table table={table} />;
};
